import React from 'react';
import { APITypes, Dynamo } from '../../../primary-record-types';
import { getIntl } from '../../../utils/intl';
import { FormStack } from '../../v2BaseComponents/Stack';
import { Field } from 'formik';
import { FormikInputs } from '../../v2BaseComponents/Inputs';
import { SelectOption } from '../../v2BaseComponents/Inputs/Select/types';
import { poaRelationshipTypeMessages } from '../../../utils/sharedMessages';
import { phoneNumber, required } from '../../../utils/validators';
import FilesField, { FileFieldValue, mapFormValueToAPIFile } from './FilesField';

const intl = getIntl('POAFormFields', {
    poaHealthcareProxyName: 'Healthcare Proxy Name',
    poaRelationship: 'Relationship',
    poaPhoneNumber: 'Phone Number',
    legalDocuments: 'Legal Documents',
    legalDocument: 'Legal Document #{index}',
});

export type FormValue = Omit<APITypes.medicalProfileService.UpsertProfilePOARequest, 'poaRelationship' | 'poaFiles'> & {
    poaFiles: FileFieldValue[];
    poaRelationship: Dynamo.POARelationship | null;
};

export const getDefaultFromValue = (
    existingRecord: APITypes.medicalProfileService.GetProfilePOAResponse,
): FormValue => {
    if (!existingRecord) {
        return {
            poaHealthcareProxyName: '',
            poaRelationship: null,
            poaPhoneNumber: '',
            poaFiles: [],
        };
    } else {
        return {
            poaHealthcareProxyName: existingRecord.poaHealthcareProxyName,
            poaRelationship: existingRecord.poaRelationship,
            poaPhoneNumber: existingRecord.poaPhoneNumber,
            poaFiles: existingRecord.poaFiles.map((file) => ({
                fileId: file.fileId,
                name: file.name,
            })),
        };
    }
};

export const formValueToUpsertProfilePOARequest = (
    values: FormValue,
    existingRecord: APITypes.medicalProfileService.GetProfilePOAResponse,
): APITypes.medicalProfileService.UpsertProfilePOARequest => {
    return {
        poaHealthcareProxyName: values.poaHealthcareProxyName,
        poaRelationship: values.poaRelationship || undefined,
        poaPhoneNumber: values.poaPhoneNumber || undefined,
        poaFiles: mapFormValueToAPIFile(values.poaFiles, existingRecord?.poaFiles || []),
    };
};

export type POAFormFieldsOwnProps = {
    values: FormValue;
};

export type POAFormFieldsProps = POAFormFieldsOwnProps;

const POAes: Dynamo.POARelationship[] = ['family', 'friend', 'professional-advisor', 'trustee'];

const POASelectOptions: SelectOption[] = POAes.map((relationship) => ({
    label: poaRelationshipTypeMessages(relationship),
    value: relationship,
}));

const POAFormFields: React.FC<POAFormFieldsProps> = (props) => {
    return (
        <FormStack>
            <Field
                name="poaHealthcareProxyName"
                title={intl('poaHealthcareProxyName')}
                component={FormikInputs.Input}
                isRequired
                validate={required}
            />
            <Field
                name="poaRelationship"
                title={intl('poaRelationship')}
                component={FormikInputs.Select}
                selectOptions={POASelectOptions}
                allowEmpty
            />
            <Field
                name="poaPhoneNumber"
                title={intl('poaPhoneNumber')}
                component={FormikInputs.PhoneNumber}
                validate={phoneNumber}
            />
            <FilesField
                values={props.values.poaFiles}
                baseFieldName="poaFiles"
                isDisabled={false}
                sectionTitle={intl('legalDocuments')}
                getFileSectionTitle={(index) => intl('legalDocument', { index: index + 1 })}
            />
        </FormStack>
    );
};

export default React.memo(POAFormFields);
